import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { MessageSquare, X, Send, Loader2, Maximize2, Minimize2 } from "lucide-react";
import axiosInstance from "../../API/interceptor";
import { Editor } from "primereact/editor";
import "primereact/resources/themes/lara-light-purple/theme.css";
import "primereact/resources/primereact.min.css";

const AVATAR_COLORS = ["#7C3AED","#0D9488","#DB2777","#D97706","#2563EB","#059669","#DC2626"];

function InitialAvatar({ name = "?", size = 34 }) {
  const bg = AVATAR_COLORS[(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];
  return (
    <div className="rounded-full shrink-0 flex items-center justify-center text-white font-bold"
      style={{ width:size, height:size, background:bg, fontSize:Math.round(size*0.36) }}>
      {(name || "?").slice(0, 2).toUpperCase()}
    </div>
  );
}

function fmtDate(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return String(ts);
    const dd   = String(d.getDate()).padStart(2, "0");
    const mm   = String(d.getMonth() + 1).padStart(2, "0");
    const h24  = d.getHours();
    const h12  = h24 % 12 || 12;
    const ampm = h24 >= 12 ? "PM" : "AM";
    const min  = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${d.getFullYear()} ${String(h12).padStart(2,"0")}:${min} ${ampm}`;
  } catch { return String(ts); }
}

export default function CommentPanel({ task, onClose, onCommented }) {
  const open = !!task;

  const [comments,     setComments]     = useState([]);
  const [text,         setText]         = useState("");
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [toast,        setToast]        = useState(null);
  const [raciMembers,  setRaciMembers]  = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [dropdownPos,  setDropdownPos]  = useState({ top: 0, left: 0, width: 300 });
  const [previewSrc,   setPreviewSrc]   = useState(null);
  const [expanded,     setExpanded]     = useState(false);

  const listRef       = useRef(null);
  const editorRef     = useRef(null);
  const editorWrapRef = useRef(null);

  // Stable refs used inside the capture-phase keyboard listener
  const mentionStateRef   = useRef({ query: null, index: 0 });
  const raciMembersRef    = useRef([]);
  const insertFnRef       = useRef(null);
  const submitFnRef       = useRef(null);
  // Exact Quill document index of the "@" that opened the mention dropdown —
  // captured once at type-time (when we know for sure the editor has focus
  // and a valid selection) and reused as-is on click. Re-deriving this by
  // walking back through quill.getText() at click-time (the old approach)
  // breaks whenever an embedded image precedes the mention, because an
  // image's length in the plain-text projection doesn't reliably line up
  // with its length in Quill's real document index — the scan lands on the
  // wrong offset, or never finds "@", and the click silently does nothing.
  const mentionAtIndexRef = useRef(null);

  // Sync refs every render so closures inside the listener always have fresh values
  mentionStateRef.current = { query: mentionQuery, index: mentionIndex };
  raciMembersRef.current  = raciMembers;

  const filteredMembers = mentionQuery !== null
    ? raciMembers.filter(m => m.toLowerCase().includes((mentionQuery || "").toLowerCase()))
    : [];

  // --- Load comments + RACI members ---
  useEffect(() => {
    if (!task?._id) {
      setComments([]); setText(""); setError(""); setRaciMembers([]);
      return;
    }
    setLoading(true); setComments([]); setError("");
    axiosInstance.get(`/task_assignment/get_task_details/${task._id}`)
      .then(res => {
        const data = res.data || {};
        const notes = Array.isArray(data.notes) ? [...data.notes].reverse() : [];
        setComments(notes);

        const seen = new Set();
        const add = (val) => {
          if (Array.isArray(val)) val.forEach(n => n && seen.add(String(n).trim()));
          else if (val) seen.add(String(val).trim());
        };
        add(data.task_created_by_name);
        add(data.assigned_to_name);
        add(data.observer_to_name);
        add(data.informer_to_name);
        setRaciMembers([...seen].filter(Boolean));
      })
      .catch(() => setError("Failed to load comments. Please try again."))
      .finally(() => setLoading(false));
  }, [task?._id]);

  // --- Capture-phase keyboard handler for mention navigation ---
  // Capture phase fires before Quill's own bubble-phase listener, so we can
  // intercept ArrowUp/Down/Enter/Escape when the mention dropdown is open.
  useEffect(() => {
    const wrapper = editorWrapRef.current;
    if (!wrapper) return;

    const handleCapture = (e) => {
      const { query, index } = mentionStateRef.current;

      // Ctrl+Enter → submit comment
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && query === null) {
        e.preventDefault(); e.stopPropagation();
        submitFnRef.current?.();
        return;
      }

      if (query === null) return;

      const members = raciMembersRef.current.filter(m =>
        m.toLowerCase().includes((query || "").toLowerCase())
      );
      if (members.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault(); e.stopPropagation();
        setMentionIndex(i => (i + 1) % members.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault(); e.stopPropagation();
        setMentionIndex(i => (i - 1 + members.length) % members.length);
      } else if (e.key === "Enter") {
        const chosen = members[index];
        if (chosen) { e.preventDefault(); e.stopPropagation(); insertFnRef.current?.(chosen); }
      } else if (e.key === "Escape") {
        e.preventDefault(); e.stopPropagation();
        mentionAtIndexRef.current = null;
        setMentionQuery(null);
      }
    };

    wrapper.addEventListener("keydown", handleCapture, true);
    return () => wrapper.removeEventListener("keydown", handleCapture, true);
  }, []);

  // Resize any image pasted into the comment editor to 25%
  useEffect(() => {
    const el = editorWrapRef.current;
    if (!el) return;
    const applySize = img => {
      img.style.width    = "25%";
      img.style.maxWidth = "25%";
      img.style.height   = "auto";
    };
    const obs = new MutationObserver(mutations => {
      mutations.forEach(m => {
        m.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          if (node.tagName === "IMG") applySize(node);
          node.querySelectorAll?.("img").forEach(applySize);
        });
      });
    });
    obs.observe(el, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  // Click any image in comment history → fullscreen preview
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onClick = e => {
      if (e.target.tagName === "IMG") {
        e.preventDefault();
        e.stopPropagation();
        setPreviewSrc(e.target.src);
      }
    };
    el.addEventListener("click", onClick, true);
    return () => el.removeEventListener("click", onClick, true);
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // A comment is only "empty" if it has no visible text AND no image embed —
  // stripping tags to check for text would otherwise also strip <img>, making
  // an image-only comment look empty and blocking it from ever being sent.
  const isEditorEmpty = (val) => {
    if (!val) return true;
    if (val.replace(/<[^>]*>/g, "").trim()) return false;
    return !/<img[\s/>]/i.test(val);
  };

  // --- Detect @mention while typing ---
  const handleTextChange = (e) => {
    const quill = editorRef.current?.getQuill();
    // Shrink any pasted image to 25% width right now, synchronously, before
    // reading the HTML that gets saved into `text` and posted to the
    // backend. The MutationObserver below does the same style change, but
    // asynchronously — by the time it runs, e.htmlValue here has already
    // captured the full-size image, so what actually gets saved (and later
    // re-rendered in comment history) was full size regardless of what the
    // observer did to the live view.
    if (quill) {
      quill.root.querySelectorAll("img").forEach(img => {
        if (img.style.width !== "25%") {
          img.style.width    = "25%";
          img.style.maxWidth = "25%";
          img.style.height   = "auto";
        }
      });
    }
    const html = quill ? quill.root.innerHTML : (e.htmlValue || "");
    setText(html);
    // Quill always appends a trailing \n to textValue — strip it before matching
    const plain = (e.textValue || "").replace(/\n+$/, "");
    const match = plain.match(/@([^\s@]*)$/);
    if (match) {
      // Capture the real Quill index of "@" right now, while the editor
      // definitely has focus and an accurate selection. match[0] ("@"+query)
      // is plain ASCII typed just now, so its length maps 1:1 onto Quill's
      // index regardless of any images earlier in the document.
      const quill = editorRef.current?.getQuill();
      const sel = quill?.getSelection();
      mentionAtIndexRef.current = sel ? sel.index - match[0].length : null;
      setMentionQuery(match[1]);
      setMentionIndex(0);
      if (editorWrapRef.current) {
        const rect = editorWrapRef.current.getBoundingClientRect();
        const estimatedHeight = Math.min(raciMembers.length * 46 + 44, 280);
        const spaceBelow = window.innerHeight - rect.bottom;
        if (spaceBelow < estimatedHeight + 8) {
          setDropdownPos({ bottom: window.innerHeight - rect.top + 4, top: null, left: rect.left, width: rect.width });
        } else {
          setDropdownPos({ top: rect.bottom + 4, bottom: null, left: rect.left, width: rect.width });
        }
      }
    } else {
      mentionAtIndexRef.current = null;
      setMentionQuery(null);
    }
  };

  // --- Insert selected member as a styled mention ---
  const insertMention = (memberName) => {
    const quill = editorRef.current?.getQuill();
    const atIdx = mentionAtIndexRef.current;
    const query = mentionStateRef.current.query ?? "";

    if (!quill || atIdx == null || atIdx < 0) { setMentionQuery(null); return; }

    // Delete exactly "@" + whatever was typed since — no re-scanning of
    // quill.getText() needed, so an image embedded earlier in the comment
    // can't throw the offset off.
    const deleteLen = 1 + query.length;
    quill.deleteText(atIdx, deleteLen);
    quill.insertText(atIdx, `@${memberName}`, { color: "#7C3AED", bold: true });
    // Insert trailing space with formatting reset
    const afterMention = atIdx + memberName.length + 1;
    quill.setSelection(afterMention);
    quill.format("color", null);
    quill.format("bold", null);
    quill.insertText(afterMention, " ");
    quill.setSelection(afterMention + 1);
    setText(quill.root.innerHTML);

    mentionAtIndexRef.current = null;
    setMentionQuery(null);
  };
  insertFnRef.current = insertMention;

  // Put HTML (e.g. a comment being restored after a failed send) back into
  // the actual Quill instance — same reasoning as the clear-on-send fix:
  // updating `text` state alone won't visually update the editor.
  const restoreEditorContent = (html) => {
    setText(html);
    const quill = editorRef.current?.getQuill();
    if (quill) quill.setContents(quill.clipboard.convert({ html }));
  };

  // --- Submit comment ---
  const handleSubmit = () => {
    if (isEditorEmpty(text)) return;
    const trimmed = text;
    const tempId = `temp_${Date.now()}`;
    const optimistic = { _id:tempId, user_name:"You", comment:trimmed, timestamp:new Date().toISOString(), isNew:true, pending:true };
    setComments(prev => [optimistic, ...prev]);
    setText("");
    // setText("") alone doesn't visually clear the box — PrimeReact's Editor
    // seeds its Quill content once from `value` and doesn't re-sync on every
    // change, so the old text/image stays on screen unless we clear the
    // actual Quill instance directly.
    editorRef.current?.getQuill()?.setText("");
    setTimeout(() => { if (listRef.current) listRef.current.scrollTop = 0; }, 50);

    axiosInstance.post(`/task_assignment/add_comment/${task._id}`, { comment:trimmed }, { headers:{"Content-Type":"application/json"} })
      .then(res => {
        if (res.data?.success) {
          setComments(prev => prev.map(c => c._id===tempId ? {...c, pending:false, user_name:res.data?.user_name||"You"} : c));
          showToast("Comment saved!", "success");
          if (onCommented) onCommented();
        } else {
          setComments(prev => prev.filter(c => c._id!==tempId));
          restoreEditorContent(trimmed);
          showToast("Failed to save comment", "error");
        }
      })
      .catch(() => {
        setComments(prev => prev.filter(c => c._id!==tempId));
        restoreEditorContent(trimmed);
        showToast("Network error — comment not saved", "error");
      });
  };
  submitFnRef.current = handleSubmit;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[1200] transition-opacity duration-[250ms] bg-black/35 backdrop-blur-[2px] ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Offcanvas */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-[min(820px,100vw)] bg-white z-[1201] flex flex-col overflow-hidden rounded-[16px_0_0_16px] shadow-[-8px_0_40px_rgba(109,40,217,.14),-2px_0_12px_rgba(0,0,0,.08)] transition-transform duration-300 ease-[cubic-bezier(.4,0,.2,1)] ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 shrink-0 bg-[linear-gradient(135deg,#7C3AED,#6D28D9)]">
          <div className="flex items-center gap-[10px]">
            <div className="w-[34px] h-[34px] rounded-[10px] bg-white/20 flex items-center justify-center">
              <MessageSquare size={16} color="#fff"/>
            </div>
            <div>
              <p className="m-0 text-[15px] font-extrabold text-white leading-[1.2]">Comments</p>
              {comments.length > 0 && (
                <p className="m-0 text-[11px] text-white/70">{comments.length} comment{comments.length!==1?"s":""}</p>
              )}
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg border-none bg-white/15 hover:bg-white/[.28] cursor-pointer flex items-center justify-center text-white transition-colors duration-150">
            <X size={14}/>
          </button>
        </div>

        {/* Task name bar */}
        <div className="px-5 py-[9px] border-b border-[#F1F5F9] bg-[#FAFBFF] shrink-0">
          <span className="text-[11px] text-[#94A3B8] font-semibold">TASK: </span>
          <span className="text-[12px] font-bold text-[#7C3AED]">{task?.name||"—"}</span>
        </div>

        {/* Compose box */}
        <div className="px-5 pt-[14px] pb-3 border-b-[1.5px] border-[#F1F5F9] shrink-0 bg-white">
          <div ref={editorWrapRef}>
            <div className="rounded-[10px] overflow-hidden border-[1.5px] border-[#E2E8F0] [&:focus-within]:border-[#7C3AED] transition-colors duration-150">
              <Editor
                ref={editorRef}
                value={text}
                onTextChange={handleTextChange}
                placeholder="Add your comment here..."
                style={{ height: expanded ? "420px" : "160px", fontSize: "13px", transition: "height .15s ease" }}
              />
            </div>
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-[#CBD5E1]">Ctrl+Enter to send · @ to mention</span>
              <button type="button" onClick={() => setExpanded(x => !x)} title={expanded ? "Collapse editor" : "Expand editor"}
                className="w-[22px] h-[22px] rounded-[6px] border border-[#E2E8F0] bg-white hover:bg-[#F8FAFC] hover:border-[#CBD5E1] cursor-pointer flex items-center justify-center text-[#94A3B8] transition-colors duration-150">
                {expanded ? <Minimize2 size={11}/> : <Maximize2 size={11}/>}
              </button>
            </div>
            <button onClick={handleSubmit} disabled={isEditorEmpty(text)}
              className={`flex items-center gap-1.5 px-[18px] py-2 rounded-lg border-none text-[13px] font-semibold transition-all duration-150 ${isEditorEmpty(text) ? "bg-[#E2E8F0] text-[#94A3B8] cursor-not-allowed" : "bg-[linear-gradient(135deg,#7C3AED,#6D28D9)] text-white cursor-pointer shadow-[0_3px_10px_rgba(109,40,217,.3)]"}`}>
              <Send size={13}/> Add Comment
            </button>
          </div>
        </div>

        {/* Comment history */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          <div className="px-5 py-[9px] shrink-0 border-b border-[#F8FAFC] flex items-center justify-between">
            <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-[.1em]">Comment History</span>
            {loading && <Loader2 size={13} color="#7C3AED" className="animate-spin"/>}
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-[14px] flex flex-col gap-3">
            {loading && [1,2,3].map(i => (
              <div key={i} className="flex gap-[10px] animate-pulse">
                <div className="w-[34px] h-[34px] rounded-full bg-[#E2E8F0] shrink-0"/>
                <div className="flex-1">
                  <div className="h-3 w-2/5 bg-[#E2E8F0] rounded-[6px] mb-2"/>
                  <div className="h-[10px] w-4/5 bg-[#F1F5F9] rounded-[6px] mb-[5px]"/>
                  <div className="h-[10px] w-3/5 bg-[#F1F5F9] rounded-[6px]"/>
                </div>
              </div>
            ))}

            {!loading && error && (
              <div className="px-4 py-3 rounded-[10px] bg-[#FEF2F2] border border-[#FECACA] text-[#DC2626] text-[13px] text-center">
                ⚠️ {error}
              </div>
            )}

            {!loading && !error && comments.length===0 && (
              <div className="flex flex-col items-center justify-center flex-1 pt-12 text-[#CBD5E1]">
                <MessageSquare size={42} className="opacity-25 mb-3"/>
                <p className="text-[13px] font-semibold m-0 text-[#94A3B8]">No comments yet</p>
                <p className="text-[11px] mt-1 m-0 text-[#CBD5E1]">Be the first to comment</p>
              </div>
            )}

            {!loading && !error && comments.map((c, i) => (
              <div key={c._id||i} className="flex gap-[10px]"
                style={{ animation: c.isNew ? "cpSlideIn .3s ease-out" : "none", opacity: c.pending ? 0.75 : 1, transition:"opacity .3s" }}>
                <InitialAvatar name={c.user_name||"?"} size={34}/>
                <div className="flex-1 min-w-0 rounded-xl px-[14px] py-[10px]"
                  style={{ background:c.isNew?"#FAF5FF":"#F8FAFC", border:`1px solid ${c.isNew?"#E9D5FF":"#F1F5F9"}`, transition:"background .4s, border-color .4s" }}>
                  <div className="flex items-center justify-between mb-[5px]">
                    <span className="text-[12px] font-bold text-[#374151]">{c.user_name||"Unknown"}</span>
                    <span className="text-[10px] text-[#94A3B8] whitespace-nowrap">
                      {c.pending
                        ? <span className="text-[#7C3AED] italic flex items-center gap-1">
                            <Loader2 size={10} className="animate-spin"/> Saving...
                          </span>
                        : fmtDate(c.timestamp)}
                    </span>
                  </div>
                  <div className="ql-editor !p-0 !text-[13px] !text-[#4B5563] !leading-[1.6] break-words [&_img]:cursor-zoom-in"
                    dangerouslySetInnerHTML={{ __html: c.comment?.includes("<") ? c.comment : c.comment?.replace(/\n/g, "<br/>") }}/>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Image lightbox — portalled to body to escape stacking context */}
        {previewSrc && createPortal(
          <div
            onClick={() => setPreviewSrc(null)}
            className="fixed inset-0 z-[999999] bg-black/85 backdrop-blur-[4px] flex items-center justify-center cursor-zoom-out"
          >
            <button
              onClick={() => setPreviewSrc(null)}
              className="absolute top-4 right-5 w-9 h-9 rounded-full bg-white/20 border-none flex items-center justify-center cursor-pointer text-white"
            >
              <X size={18}/>
            </button>
            <img
              src={previewSrc}
              alt="preview"
              onClick={e => e.stopPropagation()}
              className="max-w-[92vw] max-h-[88vh] rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.5)] cursor-default"
            />
          </div>,
          document.body
        )}

        {/* Toast */}
        {toast && (
          <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-[12px] font-semibold px-5 py-2 rounded-full whitespace-nowrap z-10 shadow-[0_4px_16px_rgba(0,0,0,.15)] [animation:cpSlideUp_.2s_ease] ${toast.type==="success" ? "bg-[linear-gradient(135deg,#059669,#047857)]" : "bg-[linear-gradient(135deg,#DC2626,#B91C1C)]"}`}>
            {toast.type==="success" ? "✅" : "❌"} {toast.msg}
          </div>
        )}
      </div>

      {/* @mention dropdown — rendered outside the offcanvas so overflow:hidden doesn't clip it */}
      {mentionQuery !== null && raciMembers.length > 0 && (
        <div className="fixed bg-white border border-[#E2E8F0] rounded-[12px] shadow-[0_8px_32px_rgba(109,40,217,.18)] overflow-y-auto max-h-[280px] z-[1300]"
          style={{
            top:    dropdownPos.top    != null ? dropdownPos.top    : "auto",
            bottom: dropdownPos.bottom != null ? dropdownPos.bottom : "auto",
            left:   dropdownPos.left,
            width:  dropdownPos.width,
          }}>
          <div className="px-3 py-[7px] border-b border-[#F1F5F9] bg-[#FAFBFF] flex items-center gap-2">
            <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-[.08em]">Task Members</span>
            {mentionQuery && (
              <span className="text-[10px] text-[#7C3AED] font-semibold bg-[#F5F3FF] px-[6px] py-[1px] rounded-full">
                {mentionQuery}
              </span>
            )}
          </div>
          {filteredMembers.length === 0 ? (
            <div className="px-4 py-3 text-[13px] text-[#94A3B8] text-center">
              No members match "{mentionQuery}"
            </div>
          ) : (
            filteredMembers.map((member, i) => (
              <div key={member}
                className={`flex items-center gap-[10px] px-3 py-[9px] cursor-pointer transition-colors duration-100 ${i === mentionIndex ? "bg-[#F5F3FF]" : "hover:bg-[#F8FAFC]"}`}
                onMouseDown={e => { e.preventDefault(); insertMention(member); }}
                onMouseEnter={() => setMentionIndex(i)}>
                <InitialAvatar name={member} size={28}/>
                <span className={`text-[13px] font-semibold ${i === mentionIndex ? "text-[#7C3AED]" : "text-[#374151]"}`}>
                  {member}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      <style>{`
        @keyframes cpSpin    { to { transform: rotate(360deg); } }
        @keyframes cpSlideIn { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes cpSlideUp { from { opacity:0; transform:translate(-50%,8px); } to { opacity:1; transform:translate(-50%,0); } }
        @keyframes cpPulse   { 0%,100% { opacity:1; } 50% { opacity:.45; } }
      `}</style>
    </>
  );
}